import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { login } from "../../api/auth";

function getFieldError(errors, field) {
  if (!errors || typeof errors !== "object") return "";
  const value = errors[field];
  if (Array.isArray(value) && value.length) return value[0];
  if (typeof value === "string") return value;
  return "";
}

function RegisterForm({ onRegistered }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setFormError("");

    try {
      if (!termsAccepted) {
        setLoading(false);
        setFormError("Please agree to the TrustBud terms to continue.");
        return;
      }
      const inviteToken = localStorage.getItem("invite_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/register/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username,
            password,
            email,
            invite_token: inviteToken || null,
            terms_accepted: termsAccepted,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setFieldErrors({
          username: getFieldError(err, "username"),
          email: getFieldError(err, "email"),
          password: getFieldError(err, "password"),
        });

        const message =
          err.detail ||
          err.non_field_errors?.[0] ||
          "Registration failed";

        setFormError(message);
        return;
      }

      await response.json();

      const tokens = await login(username, password);
      localStorage.setItem("username", username);
      localStorage.removeItem("invite_token");
      onRegistered?.(tokens);
    } catch (err) {
      console.error("Registration failed", err);
      setFormError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
            Username
          </Typography>
          <TextField
            label={null}
            placeholder="Choose a username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            fullWidth
            error={Boolean(fieldErrors.username)}
            helperText={fieldErrors.username}
            InputProps={{
              sx: {
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.9)",
              },
            }}
          />
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
            Email
          </Typography>
          <TextField
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            InputProps={{
              sx: {
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.9)",
              },
            }}
          />
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ color: "text.primary", fontWeight: 700 }}>
            Password
          </Typography>
          <TextField
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password}
            InputProps={{
              sx: {
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.9)",
              },
            }}
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
            />
          }
          label={
            <span>
              I agree to the TrustBud Terms and Community Standards.{" "}
              <Link href="/terms" target="_blank" rel="noreferrer">
                Read the terms
              </Link>
            </span>
          }
          sx={{
            alignItems: "flex-start",
            m: 0,
            p: 1.25,
            borderRadius: 2,
            backgroundColor: "rgba(255,255,255,0.72)",
          }}
        />

        {formError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {formError}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={loading || !username || !password || !email || !termsAccepted}
          size="large"
          sx={{
            borderRadius: 999,
            py: 1.35,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "0 16px 28px rgba(27, 64, 111, 0.22)",
          }}
        >
          {loading && (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          )}
          {loading ? "Creating account..." : "Create account"}
        </Button>
      </Stack>
    </form>
  );
}

export default RegisterForm;
